import { LineArrowIcon } from '@/src/assets/icons'
import { Box, Typography } from '@mui/material'
import React from 'react'
import { DataType } from '../utils'
import { useStore } from '@/src/types/store'
import {
  RANGE_CELLS,
  STAT_CELLS,
  ShareMarketData,
  formatDecimalValue,
  lastPrice,
  rangePosition
} from '@/src/utils/sharePrice'

interface TableProps {
  marketData: DataType[]
  newGraph?: boolean
  newMarketData?: ShareMarketData
}

const cellClass = 'flex flex-col gap-1 min-w-0 bg-white px-4 py-3.5 share-wide:px-6 share-wide:py-4'
const labelClass = 'text-x leading-4 font-medium text-sharePrice-muted whitespace-nowrap'
const valueClass =
  'text-[0.9375rem] leading-5 font-semibold text-sharePrice-ink whitespace-nowrap overflow-hidden text-ellipsis tabular-nums share-wide:text-xs'

// The bar colours come from --sp-accent and --sp-accent-tint, set on the card
// by MarketData from the company's Primary colour.
const SharePriceStats = ({ data }: { data?: ShareMarketData }) => {
  const last = lastPrice(data)

  return (
    <Box className="grid grid-cols-2 gap-px bg-sharePrice-hairline share-wide:grid-cols-4">
      {RANGE_CELLS.map(range => {
        const low = range.low(data)
        const high = range.high(data)
        const position = rangePosition(low, high, last)
        const percent = position === null ? null : `${(position * 100).toFixed(2)}%`
        return (
          <Box key={range.label} className={`${cellClass} col-span-2 gap-2.5`}>
            <Box className="flex justify-between gap-3">
              <span className={labelClass}>{range.label}</span>
              {last !== null && (
                <span className={`${labelClass} tabular-nums`}>
                  Last {formatDecimalValue(last)}
                </span>
              )}
            </Box>
            <Box
              className="relative h-1.5 rounded-full bg-[var(--sp-accent-tint)]"
              aria-hidden="true"
            >
              {percent && (
                <>
                  <Box
                    className="absolute inset-y-0 left-0 rounded-full bg-[var(--sp-accent)]"
                    style={{ width: percent }}
                  />
                  <Box
                    className="absolute -top-1 w-0.5 h-3.5 -ml-px rounded-full bg-sharePrice-ink"
                    style={{ left: percent }}
                  />
                </>
              )}
            </Box>
            <Box className={`flex justify-between gap-3 ${valueClass}`}>
              <span>{formatDecimalValue(low)}</span>
              <span>{formatDecimalValue(high)}</span>
            </Box>
          </Box>
        )
      })}
      {STAT_CELLS.map(stat => (
        <Box key={stat.label} className={cellClass}>
          <span className={labelClass}>{stat.label}</span>
          <span className={valueClass}>{stat.value(data)}</span>
        </Box>
      ))}
    </Box>
  )
}

const LookUpTable = ({ marketData, newGraph = false, newMarketData }: TableProps) => {
  const { statsData } = useStore()

  if (newGraph) return <SharePriceStats data={newMarketData} />

  const renderContent = (item: DataType, index: number) => {
    return (
      <Box key={index}>
        <Box className="flex  items-center gap-2">
          <Typography variant={item.header ? 'h4' : 'subtitle1'}>{item.value}</Typography>
          {item.prev_val && (
            <Box className=" md:flex  items-center gap-x-1 col-span-full">
              {statsData?.percentage?.toFixed(3) === 0 ? (
                <></>
              ) : (
                <LineArrowIcon
                  className={`${
                    statsData?.percentage?.toFixed(3) === 0
                      ? ''
                      : statsData?.percentage?.toFixed(3) < 0
                        ? 'rotate-90 w-3 h-3 fill-[#D32F2F]'
                        : '-rotate-90 w-3 h-3 fill-[#2E7D32]'
                  }`}
                />
              )}
              <Typography
                className="col-span-full"
                variant="badgeLabel"
                color={
                  statsData?.percentage?.toFixed(3) === 0
                    ? ''
                    : statsData?.percentage?.toFixed(3) < 0
                      ? 'error.main'
                      : 'success.main'
                }
              >
                {statsData?.change?.toFixed(3)}/{statsData?.percentage?.toFixed(3)}
              </Typography>
            </Box>
          )}
        </Box>
        <Typography variant="caption" color="text.secondary">
          {item.label}
        </Typography>
      </Box>
    )
  }

  return (
    <Box className="w-full rounded-[10px] bg-[#4729FF0A] border-red-600  grid grid-cols-1 md:flex flex-wrap items-center justify-between px-6 py-4">
      {marketData.map(renderContent)}
    </Box>
  )
}

export default LookUpTable
